import { ArrowRight, Layers, ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '@/utils/currency';

interface Props {
  itemCount: number;
  total: number;
  heldCount?: number;
  onViewCart: () => void;
  onCheckout: () => void;
  onHeldCarts?: () => void;
}

export default function PosMobileCheckoutBar({
  itemCount,
  total,
  heldCount = 0,
  onViewCart,
  onCheckout,
  onHeldCarts,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center gap-2 px-3 py-3">
        <button
          type="button"
          onClick={onViewCart}
          className="flex items-center gap-2.5 flex-1 min-w-0 rounded-xl px-2 py-2 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left min-h-[48px]"
        >
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-primary-700" />
            </div>
            {itemCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.1rem] text-center">
                {itemCount > 99 ? '99+' : itemCount}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-primary-700 leading-tight">
              {t('pos.viewCart')}
            </p>
            <p className="text-xs text-gray-500 leading-tight">
              {t('pos.itemCount', { count: itemCount })}
            </p>
            <p className="text-base font-bold text-gray-900 tabular-nums truncate">
              {formatMoney(total)}
            </p>
          </div>
        </button>
        {heldCount > 0 && onHeldCarts && (
          <button
            type="button"
            onClick={onHeldCarts}
            className="pos-held-toggle inline-flex flex-col items-center justify-center gap-0.5 px-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 active:bg-amber-200 min-h-[48px] min-w-[56px] shrink-0 transition-colors"
            aria-label={t('pos.heldCarts')}
          >
            <Layers className="w-5 h-5" />
            <span className="text-[11px] font-bold leading-none">{heldCount}</span>
          </button>
        )}
        <button
          type="button"
          onClick={onCheckout}
          className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-3 rounded-xl shrink-0 min-h-[48px] transition-colors"
        >
          {t('pos.checkout')}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
